export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  role: 'USER' | 'ADMIN'
  emailVerified: boolean
  createdAt: string
  addresses?: Address[]
}

export interface Address {
  id: string
  userId: string
  title: string
  province: string
  city: string
  address: string
  postalCode: string
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface Product {
  id: string
  name: string
  slug: string
  description?: string
  type: 'GOLD' | 'SILVER'
  category: 'SHMESH' | 'ABSHADE' | 'COIN' | 'BAR'
  weight: number
  purity: number
  /** وقتی مدیر معاملات فروش را استاپ کند، این مقادیر null می‌شوند (نمایش ستاره) */
  basePrice: number | null
  markupPercent: number | null
  sellingPrice: number | null
  buybackPercent?: number | null
  buybackPrice: number | null
  image?: string
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
  /** اگر true باشد یعنی قیمت‌ها ستاره‌دار هستند */
  priceHidden?: boolean
  priceHistories?: PriceHistory[]
  _count?: { orderItems: number }
}

export interface PriceHistory {
  id: string
  productId: string
  basePrice: number
  markup: number
  sellPrice: number
  buyPrice: number
  source: string
  createdAt: string
}

/** قیمت خام طلا و نقره از API (ریال) — در حالت استاپ فروش، مقادیر null و priceHidden=true می‌شوند */
export interface MarketPrices {
  goldRawPerGram: number | null
  gold24KPerGram: number | null
  gold18KPerGram: number | null
  silverRawPerGram: number | null
  source: string
  currency: 'IRR' | 'USD'
  updatedAt: string | null
  priceHidden?: boolean
}

/** وضعیت کنترل معاملات (دکمه‌های استاپ مدیر) */
export interface TradingStatus {
  /** استاپ معاملات فروش — مشتری نمی‌تواند از ما بخرد و قیمت‌ها ستاره‌دار می‌شوند */
  sellStopped: boolean
  /** استاپ معاملات خرید — مشتری نمی‌تواند به ما بفروشد و پنل فروش بسته می‌شود */
  buyStopped: boolean
  pricesHidden: boolean
  anyStopped: boolean
  updatedAt: string | null
}

export interface Order {
  id: string
  orderNumber: string
  userId: string
  user?: User
  type: 'BUY' | 'SELL'
  status: 'PENDING' | 'CONFIRMED' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED'
  subtotal: number
  tax: number
  shipping: number
  total: number
  notes?: string
  shippingAddress?: Address
  createdAt: string
  updatedAt: string
  items: OrderItem[]
}

export interface OrderItem {
  id: string
  orderId: string
  productId: string
  product?: Product
  quantity: number
  unitPrice: number
  totalPrice: number
  createdAt: string
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  errors?: Array<{ field: string; message: string }>
}

export interface CartItem {
  product: Product
  quantity: number
  /** BUY = خرید از ما | SELL = فروش به ما */
  mode: OrderType
}

export type ProductCategory = 'SHMESH' | 'ABSHADE' | 'COIN' | 'BAR'
export type ProductType = 'GOLD' | 'SILVER'
export type OrderType = 'BUY' | 'SELL'

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  SHMESH: 'شمش',
  ABSHADE: 'آب‌شده',
  COIN: 'سکه',
  BAR: 'بار'
}

export const TYPE_LABELS: Record<ProductType, string> = {
  GOLD: 'طلا',
  SILVER: 'نقره'
}

export const ORDER_STATUS_LABELS: Record<Order['status'], string> = {
  PENDING: 'در انتظار تایید',
  CONFIRMED: 'تایید شده',
  PROCESSING: 'در حال پردازش',
  COMPLETED: 'تکمیل شده',
  CANCELLED: 'لغو شده'
}

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  BUY: 'خرید',
  SELL: 'فروش'
}

/** متنی که وقتی قیمت پنهان است نمایش داده می‌شود */
export const MASKED_PRICE = '★ ★ ★'

/** نمایش قیمت با احتساب حالت پنهان (ستاره‌دار) */
export const priceText = (
  value: number | null | undefined,
  formatter: (v: number) => string
): string => (value === null || value === undefined ? MASKED_PRICE : formatter(value))